import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import Icon from 'components/Icon';
import Header from './components/Header';
import Style from '../../Carousel.module.scss';

class Carousel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      slideIndex: 0,
    };

    this.nextSlide = this.nextSlide.bind(this);
    this.previousSlide = this.previousSlide.bind(this);
    this.shouldShowArrows = this.shouldShowArrows.bind(this);
  }

  getPrevArrow() {
    const { slideIndex } = this.state;
    if (!this.shouldShowArrows() || slideIndex === 0) return null;
    const { carouselArrowOnClick } = this.props;
    return (
      <button
        className="slick-arrow slick-prev"
        type="button"
        data-role="none"
        aria-label="show previous content"
        onClick={() => {
          this.previousSlide();
          carouselArrowOnClick();
        }}
      >
        <Icon color="white" viewBox="0 0 43 44" icon="carousel-arrow-left" ariaHidden />
      </button>
    );
  }

  getNextArrow(itemsLength) {
    const { slideIndex } = this.state;
    const { carouselArrowOnClick, slidesToShow } = this.props;
    const difference = itemsLength - slideIndex;
    if (!this.shouldShowArrows() || difference <= slidesToShow) return null;
    return (
      <button
        className="slick-arrow slick-next"
        type="button"
        aria-label="show next content"
        data-role="none"
        onClick={() => {
          this.nextSlide();
          carouselArrowOnClick();
        }}
      >
        <Icon color="white" viewBox="0 0 43 44" icon="carousel-arrow-right" ariaHidden />
      </button>
    );
  }

  nextSlide() {
    this.slider.slickNext();
  }

  previousSlide() {
    this.slider.slickPrev();
  }

  shouldShowArrows() {
    const { children, showCarouselArrows, slidesToShow, useScrollableDiv } = this.props;
    const itemsLength = React.Children.count(children);
    const moreSlides = itemsLength > slidesToShow;
    const hideArrows = showCarouselArrows === false;
    const showArrows = useScrollableDiv || !children || hideArrows ? false : moreSlides;
    return showArrows;
  }

  renderItems() {
    const { addCarouselPlaceholders, children, slidesToShow } = this.props;
    const { slideIndex } = this.state;
    const itemsLength = React.Children.count(children);
    if (!children || itemsLength === 0) return null;

    const remainder = slideIndex % itemsLength;
    let itemToScaleFromLeft;
    let itemToScaleFromRight;

    if (remainder === 0) {
      itemToScaleFromRight = slidesToShow - 1;
    } else if (remainder >= slidesToShow) {
      const remainingItems = itemsLength - slideIndex;
      if (remainingItems > slidesToShow) {
        itemToScaleFromLeft = slideIndex;
        itemToScaleFromRight = slidesToShow + slideIndex - 1;
      } else {
        itemToScaleFromLeft = itemsLength - slidesToShow;
      }
    }

    const tiles = React.Children.map(children, (child, idx) =>
      React.cloneElement(child, {
        scaleFromLeft: idx === itemToScaleFromLeft,
        scaleFromRight: idx === itemToScaleFromRight,
      }),
    );

    const shouldAddPlaceholderElements =
      tiles && addCarouselPlaceholders && itemsLength < slidesToShow;
    if (shouldAddPlaceholderElements) {
      const placeholderCount = slidesToShow - itemsLength;
      for (let i = 0; i < placeholderCount; i += 1) {
        tiles.push(<div key={`placeholder-${i}`} className="carouselPlaceholder" />);
      }
    }

    return tiles;
  }

  renderHeader() {
    const { featuredTitleImage, headline, isFirst } = this.props;
    return (
      <Header
        featuredTitleImage={featuredTitleImage}
        headline={headline}
        isFirst={isFirst}
        theme={Style}
      />
    );
  }

  renderSliderDesktop() {
    const {
      autoplaySpeed,
      allowScrollLock,
      isInfinite,
      panel,
      slidesToShow,
      disableScroll,
      updateVisibleSlides,
      slidesToScroll,
    } = this.props;

    let autoplaySettings = {};

    if (autoplaySpeed) {
      autoplaySettings = {
        autoplay: true,
        autoplaySpeed,
      };
    }

    const useTransform = true;
    const useCSS = true;

    const settings = {
      afterChange: slideIndex => {
        if (allowScrollLock) {
          disableScroll({ disableScroll: false, bodyOnly: true });
        }
        this.setState({ slideIndex });
      },
      arrows: false,
      draggable: this.shouldShowArrows(),
      dots: false,
      beforeChange: (prevSlide, nextSlide) => updateVisibleSlides(prevSlide, nextSlide, panel),
      infinite: isInfinite,
      slidesToShow,
      speed: 500,
      test: 'carousel-wrapper',
      slidesToScroll,
      ...autoplaySettings,
      useTransform,
      useCSS,
      lazyLoad: true,
    };

    return (
      <>
        {this.renderHeader()}
        <Slider
          ref={s => {
            this.slider = s;
          }}
          {...settings}
          trayProps={{ role: 'listbox' }}
          data-test="shared-carousel"
        >
          {this.renderItems()}
        </Slider>
      </>
    );
  }

  renderSliderMobile() {
    return (
      <div>
        {this.renderHeader()}
        <div className={Style.scrollableDiv}>{this.renderItems()}</div>
      </div>
    );
  }

  render() {
    const isAllCarouselItemsNull = this.renderItems().every(element => element === null);
    if (isAllCarouselItemsNull) return null;
    const { children, slidesToShow, useScrollableDiv } = this.props;
    const itemsLength = React.Children.count(children);
    const sliderContainer = [Style.container, Style.roundArrows];
    if (slidesToShow > itemsLength) {
      sliderContainer.push(Style.justifyLeft);
    }

    const slider = useScrollableDiv ? this.renderSliderMobile() : this.renderSliderDesktop();

    return (
      <div className={Style.carouselContainer}>
        {this.getPrevArrow()}
        <div className={classNames(sliderContainer)}>{slider}</div>
        {this.getNextArrow(itemsLength)}
      </div>
    );
  }
}

Carousel.defaultProps = {
  addCarouselPlaceholders: false,
  allowScrollLock: false,
  autoplaySpeed: null,
  carouselArrowOnClick: () => {},
  children: null,
  disableScroll: () => {},
  featuredTitleImage: null,
  headline: {},
  isFirst: false,
  isInfinite: true,
  panel: {},
  showCarouselArrows: true,
  slidesToScroll: 1,
  slidesToShow: 3,
  updateVisibleSlides: () => {},
  useScrollableDiv: false,
};

Carousel.propTypes = {
  addCarouselPlaceholders: PropTypes.bool,
  allowScrollLock: PropTypes.bool,
  autoplaySpeed: PropTypes.number,
  carouselArrowOnClick: PropTypes.func,
  children: PropTypes.node,
  disableScroll: PropTypes.func,
  featuredTitleImage: PropTypes.string,
  headline: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  isFirst: PropTypes.bool,
  isInfinite: PropTypes.bool,
  panel: PropTypes.shape({}),
  showCarouselArrows: PropTypes.bool,
  slidesToScroll: PropTypes.number,
  slidesToShow: PropTypes.number,
  updateVisibleSlides: PropTypes.func,
  useScrollableDiv: PropTypes.bool,
};

export default Carousel;
